/**
 * Bots.jsx — default screen.
 *
 * Balance and P&L in one glance, then the list. Someone opening this on a
 * phone wants to know what their bots are doing, not to browse.
 *
 * No charts anywhere. That is what keeps recharts out of the bundle, and on
 * a 375px screen three numbers read better than a sparkline.
 */

import React, { useEffect, useState } from "react";
import * as api from "../lib/api";
import { C, fUsd, pnlColor, Label, Panel, Button, Empty, Chip,
         Skeleton, SkeletonRow } from "../theme";
import { useCountUp, useFlash } from "../lib/motion";

// A test account here has 346 bots. Rendering all of them produces a scroll
// nobody finishes, so show the ones that matter and let the rest be opened
// deliberately.
const INITIAL_VISIBLE = 12;

export default function Bots({ onOpen, onCreate, onAccount, onDeposit }) {
  const [bots, setBots] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [b, w] = await Promise.all([
          api.listBots(),
          api.balance().catch(() => null),   // balance is nice-to-have
        ]);
        setBots(Array.isArray(b) ? b : b?.bots || []);
        if (w) setWallet(w);
        setErr(null);
      } catch (e) {
        setErr(e.message);
      }
    };
    load();
    return api.poll(load, 10000);
  }, []);

  // Derived BEFORE the early returns below, because the hooks that follow
  // must run on every render — including the first, when bots is still null.
  //
  // NOTE: total_profit_loss, not pnl — that is the copybot API's field name.
  const total = (bots || []).reduce(
    (s, b) => s + Number(b.total_profit_loss || 0), 0);
  const bal = wallet
    ? Number(wallet.balance ?? wallet.usdc ?? wallet.available ?? 0)
    : null;

  // The screen polls every 10s, so figures move with no interaction to
  // explain them. Easing the change reads as an update; a flash marks which
  // direction it went.
  const animTotal = useCountUp(total);
  const animBal = useCountUp(bal ?? 0);
  const flashTotal = useFlash(total);

  if (err && !bots) return <Empty><span style={{ color: C.rose }}>{err}</span></Empty>;
  if (!bots) {
    return (
      <div style={{ padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      marginBottom: 18 }}>
          <div><Skeleton w={70} h={9} style={{ marginBottom: 8 }} />
               <Skeleton w={120} h={26} /></div>
          <div><Skeleton w={70} h={9} style={{ marginBottom: 8 }} />
               <Skeleton w={120} h={26} /></div>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 22 }}>
          <Skeleton w={78} h={20} /><Skeleton w={68} h={20} /><Skeleton w={78} h={20} />
        </div>
        {[0,1,2,3,4].map((i) => <SkeletonRow key={i} />)}
      </div>
    );
  }

  const live    = bots.filter((b) => b.mode === "live");
  const paper   = bots.filter((b) => b.mode === "paper");
  const running = bots.filter((b) => b.status === "running");


  // Running first, then anything with activity, then the rest. A stopped bot
  // with zero trades is the least interesting thing on the screen.
  const ranked = [...bots].sort((a, b) => {
    const ar = a.status === "running" ? 0 : 1;
    const br = b.status === "running" ? 0 : 1;
    if (ar !== br) return ar - br;
    return (b.total_copied_trades || 0) - (a.total_copied_trades || 0);
  });
  const visible = showAll ? ranked : ranked.slice(0, INITIAL_VISIBLE);


  return (
    <div style={{ padding: 14 }}>

      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", marginBottom: 14, gap: 10 }}>
        <div onClick={onDeposit} className="pe-tap" style={{ cursor: "pointer" }}>
          <Label>
            BALANCE <span style={{ color: C.amber, letterSpacing: 0 }}>›</span>
          </Label>
          <div style={{ fontFamily: C.disp, fontSize: 30, color: C.ink, lineHeight: 1 }}>
            {bal === null ? "—" : fUsd(animBal, false)}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <Label>TOTAL P&amp;L</Label>
          <div className={flashTotal}
               style={{ fontFamily: C.disp, fontSize: 30, lineHeight: 1,
                        color: pnlColor(total), padding: "0 4px",
                        margin: "0 -4px" }}>
            {fUsd(animTotal)}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        <Chip color={running.length ? C.green : C.dim}>{running.length} RUNNING</Chip>
        <Chip>{live.length} LIVE</Chip>
        <Chip>{paper.length} PAPER</Chip>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.25fr 1fr 1fr",
                    gap: 7, marginBottom: 18 }}>
        <Button onClick={onCreate}>+ New bot</Button>
        <Button variant="ghost" onClick={onDeposit}>Deposit</Button>
        <Button variant="ghost" onClick={onAccount}>Account</Button>
      </div>

      <Label>YOUR BOTS</Label>

      {bots.length === 0 && (
        <Empty>
          No bots yet.
          <div style={{ marginTop: 16 }}>
            <Button onClick={onCreate}>Create your first bot</Button>
          </div>
        </Empty>
      )}

      {visible.map((b) => {
        const pnl = Number(b.total_profit_loss || 0);
        const accent = b.status === "running"
          ? (pnl < 0 ? C.rose : C.green)
          : C.dim;
        return (
          <Panel key={b.bot_id} accent={accent} onClick={() => onOpen(b.bot_id)}
                 className="pe-row" style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "baseline", marginBottom: 5, gap: 8 }}>
              <span style={{ fontSize: 13, color: C.ink, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {b.bot_name || b.trader_name || b.bot_id}
              </span>
              <span style={{ fontSize: 14, color: pnlColor(pnl), fontWeight: 500,
                             flexShrink: 0 }}>
                {fUsd(pnl)}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, fontSize: 11, color: C.dim,
                          flexWrap: "wrap" }}>
              <span>{String(b.mode || "").toUpperCase()}</span>
              <span className={b.status === "running" ? "pe-pulse" : ""}
                    style={{ color: b.status === "running" ? C.green : C.dim }}>
                · {b.status}
              </span>
              <span>· {b.total_copied_trades || 0} trades</span>
              {b.win_rate ? <span>· {Number(b.win_rate).toFixed(0)}% win</span> : null}
            </div>
          </Panel>
        );
      })}

      {!showAll && ranked.length > INITIAL_VISIBLE && (
        <Button variant="quiet" onClick={() => setShowAll(true)}
                style={{ marginTop: 4 }}>
          Show all {ranked.length} bots
        </Button>
      )}

      {/* Fee transparency. On the free tier the user pays 1% per trade, so
          showing what that costs is honest — and it becomes the upgrade
          prompt without being a purchase CTA, which Telegram's rules on
          digital goods would not permit. */}
      <div style={{ border: `1px solid ${C.line}`,
                    background: "var(--pe-bg2, #12100c)", padding: "11px 12px",
                    marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: C.dim }}>Taker fee</span>
          <span style={{ fontSize: 12, color: C.ink }}>1.0%</span>
        </div>
        <p style={{ fontSize: 10.5, color: C.dim, margin: 0, lineHeight: 1.6 }}>
          Free tier. A paid plan removes it — plans are managed at
          polyedge.online.
        </p>
      </div>
    </div>
  );
}
